import React, { CSSProperties, SyntheticEvent, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Form, FormField, FormGroup, Icon, Input } from "semantic-ui-react";

// Component
import TableIssueList from "./TableIssueList";
import PanelNewIssue from "./PanelNewIssue";
import ModalIssueHistory from "./ModalIssueHistory";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import { DateTextBox } from "react-lib/apps/common";
import { ComponentPropsGetterR } from "react-table-6";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";

// Utils
import moment from "moment";
import { formatISOtoBEDateTime, dateToStringWithoutTimeBE } from "react-lib/utils/dateUtils";

// Interface
import {
  CARD_NAME,
  BTN_ACTS,
  MasterOptions,
  OnEvent,
  PickedState,
  RunSequence,
  SetProp,
  State,
  FilterType,
  IssueListType,
  JournalDetailType,
  DEFAULT_LIMIT,
} from "./sequence/IssueList";

import { getCustomFieldIdByName } from "./sequence/ProjectList";

import { CreateIssueType } from "./sequence/RedmineAPIController";

// type
type CardIssueListProps = {
  onEvent: OnEvent;
  setProp: SetProp;
  runSequence: RunSequence;
  IssueListSequence: State["IssueListSequence"];
  masterOptions: MasterOptions;
  languageUX: "en" | "th";
} & Pick<PickedState, "buttonLoadCheck" | "errorMessage" | "successMessage">;

type RenderStatusProps = {
  status: string;
};

type RenderCreatedNUpdatedProps = Partial<{
  date: string;
  author: string;
}>;

type RenderSolutionsProps = {
  text?: string;
};

type FormatOptionsType = Partial<{
  statusMap: Record<string, string>;
  priorityMap: Record<string, string>;
  trackerMap: Record<string, string>;
  projectMap: Record<string, string>;
  userMap: Record<string, string>;
  customFieldMap: Record<string, string>;
}>;

const styles: Record<string, CSSProperties> = {
  itemCenter: { display: "flex", justifyContent: "center" },
};

const STATUS_COLOR_MAP: Record<string, string> = {
  New: "#000000",
  "In Progress": "#EAB308",
  Resolved: "#1B9D2C",
};

const DEFAULT_FORMSTATE: CreateIssueType = {
  id: undefined,
  subject: "",
  description: "",
  tracker_id: undefined,
  priority_id: undefined,
  assigned_to_id: undefined,
  project_id: undefined,
  status_id: undefined,
  start_date: dateToStringWithoutTimeBE(moment()),
  due_date: undefined,
  custom_fields: [],
  notes: "",
};

const RenderStatus = (props: RenderStatusProps) => {
  const color = STATUS_COLOR_MAP[props.status] || "#000000";

  return <label style={{ color }}>{props.status}</label>;
};

const RenderCreatedNUpdated = (props: RenderCreatedNUpdatedProps) => {
  const date = formatISOtoBEDateTime(props.date).dateTimeShort || "";
  const author = props.author || "";

  if (!author) {
    return <label>{date}</label>;
  }

  return <label>{`${author}, ${date}`}</label>;
};

const RenderSolutions = (props: RenderSolutionsProps) => {
  const text = props.text || "";

  return (
    <div
      style={{
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        lineHeight: "1.4em",
        maxHeight: "2.8em",
        width: "100%",
        whiteSpace: "normal",
      }}
    >
      {text}
    </div>
  );
};

const CardIssueList = (props: CardIssueListProps) => {
  const [openPanel, setOpenPanel] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [filterSearch, setFilterSearch] = useState<FilterType>({});
  const [formState, setFormState] = useState<CreateIssueType>(DEFAULT_FORMSTATE);

  useEffect(() => {
    props.runSequence({ sequence: "IssueList", restart: true });
  }, []);

  // Clear Data When Save Success
  useEffect(() => {
    if (props.successMessage?.[CARD_NAME]) {
      handleClosePanel();
    }
  }, [props.successMessage]);

  const getProjectOptions = (isFilter: boolean = false) => {
    const list = props.IssueListSequence?.projectList || [];

    const filtered = list
      .filter((project) => {
        const divisionField = project.custom_fields?.find((field) => field.name === "Division");
        const activeField = project.custom_fields?.find((field) => field.name === "Active");
        const isActive = activeField?.value === "1";
        const onlySubProj = !!project.parent;
        const allowedDivisions = divisionHasUserOptions.map((d) => d.text);

        if (!divisionField || !Array.isArray(divisionField.value)) return false;

        return (
          isActive &&
          onlySubProj &&
          divisionField.value.some((divName: string) => allowedDivisions.includes(divName))
        );
      })
      .map((project) => ({
        key: project.id,
        value: project.id || 0,
        text: project.name,
      }));

    return isFilter ? [{ key: 0, value: 0, text: "ALL" }, ...filtered] : filtered;
  };

  const getAssignedDivisionOptions = (isFilter: boolean = false) => {
    const list =
      customFieldList?.find((f: any) => f.name === "Assigned Division")?.possible_values || [];

    const optionsMaps = list.map((division, index) => ({
      key: index,
      value: division.value,
      text: division.label,
    }));

    return isFilter ? [{ key: 0, value: 0, text: "ALL" }, ...optionsMaps] : optionsMaps;
  };

  const isEdit = useMemo(() => !!formState?.id, [formState]);

  const divisionHasUserOptions = useMemo(
    () => props.IssueListSequence?.divisionHasUser || [],
    [props.IssueListSequence]
  );

  const currentDivisionText = useMemo(() => {
    const divisions = props.masterOptions?.division || [];
    const currentDivision = props.IssueListSequence?.currentDivision;

    return divisions.find((division) => division.value === currentDivision)?.text || "";
  }, [props.IssueListSequence?.currentDivision, props.masterOptions]);

  const activePage = useMemo(
    () => props.IssueListSequence?.pagination?.activePage || 1,
    [props.IssueListSequence]
  );

  const issueList = useMemo(
    () => props.IssueListSequence?.pagination?.items || [],
    [props.IssueListSequence]
  );

  const historyList = useMemo(
    () => props.IssueListSequence?.historyList || [],
    [props.IssueListSequence]
  );

  const customFieldList = useMemo(
    () => props.IssueListSequence?.customFields || [],
    [props.IssueListSequence]
  );

  const trackerOptions = useMemo(
    () => mapToOptions(props.IssueListSequence?.trackerList || []),
    [props.IssueListSequence?.trackerList]
  );

  const priorityOptions = useMemo(
    () => mapToOptions(props.IssueListSequence?.priorityList || []),
    [props.IssueListSequence?.priorityList]
  );

  const membershipOptions = useMemo(() => {
    const options = mapToOptions(props.IssueListSequence?.membershipList || []);

    return [{ key: 0, value: 0, text: "ALL" }, ...options];
  }, [props.IssueListSequence?.membershipList]);

  const membershipProjectOptions = useMemo(
    () => mapToOptions(props.IssueListSequence?.membershipProjectList || []),
    [props.IssueListSequence?.membershipProjectList]
  );

  const issueStatusFilterOptions = useMemo(() => {
    const options = mapToOptions(props.IssueListSequence?.statusList || []);

    return [{ key: 0, value: 0, text: "ALL" }, ...options];
  }, [props.IssueListSequence?.statusList]);

  const issueStatusCreateOptions = useMemo(() => {
    const list = props.IssueListSequence?.statusList || [];
    const isEdit = !!formState.project_id;

    const filtered = isEdit ? list : list.filter((status) => status.id === 1);

    return mapToOptions(filtered);
  }, [props.IssueListSequence?.statusList, formState?.priority_id]);

  const projectFilterOptions = useMemo(
    () => getProjectOptions(true),
    [props.IssueListSequence?.projectList, currentDivisionText]
  );

  const projectPanelOptions = useMemo(
    () => getProjectOptions(),
    [props.IssueListSequence?.projectList, currentDivisionText]
  );

  const projectIdList = useMemo(() => {
    const ids = projectPanelOptions.map((opt) => opt.value);

    return ids.length > 0 ? ids : [0];
  }, [projectPanelOptions]);

  const divisionOptions = useMemo(() => {
    if (!formState?.project_id) return [];

    const selectedProject = props.IssueListSequence?.projectList?.find(
      (proj) => proj.id === formState.project_id
    );

    const divisionField = selectedProject?.custom_fields?.find(
      (field) => field.name === "Division"
    );

    if (!divisionField || !Array.isArray(divisionField.value)) return [];

    return divisionField.value.map((division) => ({
      key: division,
      value: division,
      text: division,
    }));
  }, [props.IssueListSequence?.customFieldsDivision, formState?.project_id]);

  const assignedDivisionFilterOptions = useMemo(() => getAssignedDivisionOptions(true), [customFieldList]);
  const assignedDivisionPanelOptions = useMemo(() => getAssignedDivisionOptions(), [customFieldList]);

  const trackerMap = useMemo(() => createValueMap(trackerOptions), [trackerOptions]);
  const priorityMap = useMemo(() => createValueMap(priorityOptions), [priorityOptions]);
  const userMap = useMemo(
    () => createValueMap([...membershipOptions, ...membershipProjectOptions]),
    [membershipOptions, membershipProjectOptions]
  );
  const statusMap = useMemo(
    () => createValueMap(issueStatusFilterOptions),
    [issueStatusFilterOptions]
  );
  const projectMap = useMemo(() => createValueMap(projectPanelOptions), [projectPanelOptions]);
  const customFieldMap = useMemo(() => {
    return {
      [getCustomFieldIdByName(customFieldList, "Division", "issue") ?? ""]: "Division",
      [getCustomFieldIdByName(customFieldList, "Causes and Solutions", "issue") ?? ""]:
        "Causes and Solutions",
      [getCustomFieldIdByName(customFieldList, "Assigned Division", "issue") ?? ""]:
        "Assigned Division",
    };
  }, [customFieldList]);

  const formattedItems = useMemo(
    () =>
      issueList.map((item) => {
        return {
          ...item,
          _id: <div style={styles.itemCenter}>{item.id}</div>,
          tracker: <div style={styles.itemCenter}>{item?.tracker?.name || ""}</div>,
          status: (
            <div style={styles.itemCenter}>
              <RenderStatus status={item?.status?.name || ""} />
            </div>
          ),
          priority: <div style={styles.itemCenter}>{item?.priority?.name || ""}</div>,
          subject: item?.subject,
          assigned_division: (
            <div style={styles.itemCenter}>
              {item?.custom_fields?.find((f: any) => f.name === "Assigned Division")?.value || ""}
            </div>
          ),
          assigned_to: <div style={styles.itemCenter}>{item?.assigned_to?.name || ""}</div>,
          solutions: (
            <RenderSolutions
              text={
                item?.custom_fields?.find((f: any) => f.name === "Causes and Solutions")?.value ||
                ""
              }
            />
          ),
          created: (
            <div style={styles.itemCenter}>
              <RenderCreatedNUpdated date={item?.created_on} author={item?.author?.name} />
            </div>
          ),
          updated: (
            <div style={styles.itemCenter}>
              <RenderCreatedNUpdated date={item?.updated_on} />
            </div>
          ),
          project: <div style={styles.itemCenter}>{item?.project?.name || ""}</div>,
        };
      }),
    [issueList]
  );

  const formattedHistory = useMemo(
    () =>
      historyList.map((item) => {
        const formattedDetailList = formatJournalDetails(item.details, item.notes, {
          statusMap,
          priorityMap,
          trackerMap,
          projectMap,
          userMap,
          customFieldMap,
        });

        return {
          ...item,
          created_on: (
            <div style={styles.itemCenter}>
              {formatISOtoBEDateTime(item.created_on).dateTimeShort || ""}
            </div>
          ),
          details: (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {formattedDetailList.map((line, idx) => (
                <div key={idx} dangerouslySetInnerHTML={{ __html: line }} />
              ))}
            </div>
          ),
          user: <div style={styles.itemCenter}>{item.user?.name || ""}</div>,
        };
      }),
    [historyList, statusMap, priorityMap, trackerMap, projectMap, userMap]
  );

  const handleGetTrProps: ComponentPropsGetterR = (state, rowInfo) => {
    if (!rowInfo) return {};

    const originalId = rowInfo?.original?.id;
    const isSelected = originalId === formState.id;

    return {
      style: {
        backgroundColor: isSelected ? "#e0f7fa" : "",
      },
      onClick: () => {
        const items = issueList || [];
        const foundItem = items.find((item) => item.id === originalId);

        if (foundItem) {
          handleSelectItem(foundItem);
        }
      },
    };
  };

  const handleSelectItem = (item: IssueListType) => {
    setOpenPanel(true);

    if (item.project?.id) {
      props.runSequence({
        sequence: "IssueList",
        action: "MEMBERSHIP",
        projectId: item.project.id,
      });
    }

    const formatSelectedItem: CreateIssueType = {
      id: item.id,
      subject: item.subject,
      description: item.description,
      tracker_id: item.tracker?.id,
      priority_id: item.priority?.id,
      assigned_to_id: item.assigned_to?.id,
      project_id: item.project?.id,
      status_id: item.status?.id,
      custom_fields: item.custom_fields || [],
      start_date: item.start_date ? formatISOtoBEDateTime(item.start_date).date : undefined,
      due_date: item.due_date ? formatISOtoBEDateTime(item.due_date).date : undefined,
    };

    setFormState(formatSelectedItem);
  };

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const { value, name } = data;

    let newFilterSearch = {
      ...filterSearch,
      [name]: value,
    };

    setFilterSearch(newFilterSearch);
  };

  const handleChangeData = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const { value, name } = data;

    if (name === "project_id") {
      props.runSequence({
        sequence: "IssueList",
        action: "MEMBERSHIP",
        projectId: value,
      });

      setFormState((prev) => ({
        ...prev,
        [name]: value,
        assigned_to_id: undefined,
        custom_fields: prev.custom_fields?.filter((f) => f.name !== "Division") || [],
      }));

      return;
    }

    if (name === "division") {
      updateCustomField("Division", value);
      return;
    }

    if (name === "causes_and_solutions") {
      updateCustomField("Causes and Solutions", value);
      return;
    }

    if (name === "assigned_division") {
      updateCustomField("Assigned Division", value);
      return;
    }

    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const updateCustomField = (name: string, value: any) => {
    const currentFields = [...(formState.custom_fields || [])];
    const index = currentFields.findIndex((f) => f.name === name);

    if (index !== -1) {
      currentFields[index] = { ...currentFields[index], value };
    } else {
      currentFields.push({ name, value });
    }

    setFormState((prev) => ({
      ...prev,
      custom_fields: currentFields,
    }));
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "IssueList",
      action: "SEARCH",
      filter: filterSearch,
      projectIdList,
    });
  };

  const handleExportExcel = () => {
    props.runSequence({
      sequence: "IssueList",
      action: "EXPORT_EXCEL",
      data: issueList,
    });
  };

  const handlePageChange = (activePage: number) => {
    props.runSequence({
      sequence: "IssueList",
      action: "SEARCH",
      filter: filterSearch,
      activePage,
      projectIdList,
    });
  };

  const handleCreate = () => {
    props.runSequence({
      sequence: "IssueList",
      action: "CREATE",
      createdData: formState,
      projectIdList,
    });
  };

  const handleEdit = () => {
    if (!formState?.id) {
      return;
    }

    props.runSequence({
      sequence: "IssueList",
      action: "EDIT",
      issueId: formState.id,
      editedData: formState,
      activePage,
      projectIdList,
    });
  };

  const handleOpenPanel = () => {
    if (isEdit) {
      setFormState(DEFAULT_FORMSTATE);
    }

    setOpenPanel(true);
  };

  const handleClosePanel = () => {
    setOpenPanel(false);
    setFormState(DEFAULT_FORMSTATE);
  };

  const handleOpenModal = () => {
    if (!formState.id) {
      return;
    }

    setOpenModal(true);

    props.runSequence({
      sequence: "IssueList",
      action: "HISTORY",
      issueId: formState.id,
    });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      {/* Filter Section */}
      <Form style={{ margin: "1rem" }}>
        {/* First Section */}
        <FormGroup inline>
          <FormField inline>
            <label>Selected Project</label>
          </FormField>
          <FormField inline width={2}>
            <div style={{ width: "100%" }}>
              <Dropdown
                fluid
                selection
                value={filterSearch.project_id ?? 0}
                name="project_id"
                onChange={handleChangeFilter}
                options={projectFilterOptions}
              />
            </div>
          </FormField>
          <FormField inline>
            <label>Created</label>
          </FormField>
          <FormField inline width={2}>
            <DateTextBox
              value={filterSearch.start_date || ""}
              onChange={(value: any) => handleChangeFilter(null, { name: "start_date", value })}
            />
          </FormField>
          <FormField inline>
            <label>To</label>
          </FormField>
          <FormField inline width={2}>
            <DateTextBox
              value={filterSearch.end_date || ""}
              onChange={(value: any) => handleChangeFilter(null, { name: "end_date", value })}
            />
          </FormField>
          <FormField inline>
            <label>Search Subject</label>
          </FormField>
          <FormField inline width={2}>
            <div style={{ width: "100%" }}>
              <Input
                fluid
                name="subject"
                value={filterSearch.subject || ""}
                onChange={handleChangeFilter}
              />
            </div>
          </FormField>
          <FormField inline>
            <label>Status</label>
          </FormField>
          <FormField inline style={{ width: "10rem" }}>
            <div style={{ width: "100%" }}>
              <Dropdown
                fluid
                selection
                name="status_id"
                value={filterSearch.status_id ?? 0}
                onChange={handleChangeFilter}
                options={issueStatusFilterOptions}
              />
            </div>
          </FormField>
          <FormField width={2}>
            <div style={{ width: "100%", display: "flex" }}>
              <div style={{ marginLeft: "auto" }}>
                <Button color="green" onClick={handleOpenPanel}>
                  <div>
                    <Icon name="plus" />
                    <label>New Issue</label>
                  </div>
                </Button>
              </div>
            </div>
          </FormField>
        </FormGroup>

        {/* Second Section */}
        <FormGroup inline>
          <FormField inline>
            <label>Author</label>
          </FormField>
          <FormField inline width={2}>
            <div style={{ width: "100%" }}>
              <Dropdown
                fluid
                selection
                name="author_id"
                value={filterSearch.author_id ?? 0}
                onChange={handleChangeFilter}
                options={membershipOptions}
              />
            </div>
          </FormField>
          <FormField inline>
            <label>Assign To</label>
          </FormField>
          <FormField inline width={2}>
            <div style={{ width: "100%" }}>
              <Dropdown
                fluid
                selection
                name="assigned_to_id"
                value={filterSearch.assigned_to_id ?? 0}
                onChange={handleChangeFilter}
                options={membershipOptions}
              />
            </div>
          </FormField>
          <FormField inline>
            <label>Assigned Division</label>
          </FormField>
          <FormField inline width={2}>
            <div style={{ width: "100%" }}>
              <Dropdown
                fluid
                selection
                name="cf_6"
                value={filterSearch.cf_6 ?? 0}
                onChange={handleChangeFilter}
                options={assignedDivisionFilterOptions}
              />
            </div>
          </FormField>
          <FormField>
            <div style={{ width: "100%" }}>
              <ButtonLoadCheck
                // func
                setProp={props.setProp}
                onClick={handleSearch}
                // data
                paramKey={BTN_ACTS.SEARCH}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
                // config
                name="SEARCH"
                title="SEARCH"
                color="blue"
              />
            </div>
          </FormField>
          <FormField>
            <div style={{ width: "100%" }}>
              <ButtonLoadCheck
                // func
                setProp={props.setProp}
                onClick={handleExportExcel}
                // data
                paramKey={BTN_ACTS.EXPORT_EXCEL}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.EXPORT_EXCEL]}
                // config
                name="EXPORT_EXCEL"
                title="Download xlsx"
                color="green"
              />
            </div>
          </FormField>
        </FormGroup>
      </Form>

      {/* Filter Section */}
      <div style={{ paddingLeft: "10px", paddingRight: "10px" }}>
        <TableIssueList
          onGetTrProps={handleGetTrProps}
          data={formattedItems}
          style={
            openPanel
              ? {
                  flex: "1 1 auto",
                  overflow: "auto",
                  height: "15vh",
                  transition: "max-height 0.3s ease",
                }
              : {
                  height: "calc(90vh - 13rem)",
                  overflow: "auto",
                }
          }
        />
      </div>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginRight: "1rem",
          marginTop: "0.75rem",
        }}
      >
        <PaginationShorthand
          activePage={props.IssueListSequence?.pagination?.activePage || 1}
          limit={DEFAULT_LIMIT}
          total={props.IssueListSequence?.pagination?.total}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Panel Section */}
      {openPanel && (
        <div
          style={{
            height: "calc(54vh - 2rem)",
            overflow: "auto",
            marginTop: "1rem",
          }}
        >
          <PanelNewIssue
            key={isEdit ? `edit-${formState.id}` : `new`}
            isEdit={isEdit}
            // func
            onChange={handleChangeData}
            onCreate={handleCreate}
            onEdit={handleEdit}
            onClose={handleClosePanel}
            // History Modal
            onOpenModal={handleOpenModal}
            // data
            data={formState}
            currentDivision={currentDivisionText}
            // options
            projectOptions={projectPanelOptions}
            trackerOptions={trackerOptions}
            statusOptions={issueStatusCreateOptions}
            priorityOptions={priorityOptions}
            userOptions={membershipProjectOptions}
            divisionOptions={divisionOptions}
            assignedDivisionOptions={assignedDivisionPanelOptions}
            // common
            setProp={props.setProp}
            buttonLoadCheck={props.buttonLoadCheck}
          />
        </div>
      )}

      {/* History Modal Section */}
      {openModal && (
        <div>
          <ModalIssueHistory
            // func
            onOpen={openModal}
            onClose={handleCloseModal}
            // data
            data={formattedHistory}
          />
        </div>
      )}
    </div>
  );
};

/*================================ UTILS ==============================*/

const mapToOptions = <T extends { id: number; name: string }>(list: T[] = []) => {
  return list.map((item) => ({
    key: item.id,
    value: item.id,
    text: item.name,
  }));
};

const createValueMap = (options: Partial<{ value: any; text: string }>[]) => {
  const map: Record<string, string> = {};

  options.forEach((opt) => {
    if (opt.value && opt.text) {
      map[String(opt.value)] = opt.text;
    }
  });

  return map;
};

const setTextBold = (text: string) => `<b>${text}</b>`;

const displayChange = (label: string, oldValue?: string, newValue?: string) => {
  if (!oldValue && newValue) return `${setTextBold(label)} set to ${newValue}`;
  if (oldValue && !newValue) return `${setTextBold(label)} removed ${oldValue}`;

  return `${setTextBold(label)} changed from ${oldValue} to ${newValue}`;
};

const formatJournalDetails = (
  details: JournalDetailType[] | undefined,
  notes?: string,
  options?: FormatOptionsType
) => {
  const result: string[] = [];

  for (const detail of details || []) {
    const { property, name, old_value, new_value } = detail;

    const mapValue = (value?: string, map?: Record<string, string>) =>
      value && map ? map[value] ?? value : value;

    if (property === "attr") {
      switch (name) {
        case "project_id":
          result.push(
            `${setTextBold("Project:")} changed from ${mapValue(
              old_value,
              options?.projectMap
            )} to ${mapValue(new_value, options?.projectMap)}`
          );
          break;
        case "tracker_id":
          result.push(
            `${setTextBold("Tracker:")} changed from ${mapValue(
              old_value,
              options?.trackerMap
            )} to ${mapValue(new_value, options?.trackerMap)}`
          );
          break;
        case "subject":
          result.push(`${setTextBold("Subject:")} changed from ${old_value} to ${new_value}`);
          break;
        case "description":
          const prefixMatchLength = (() => {
            if (!old_value || !new_value) return 0;
            let i = 0;
            while (i < old_value.length && old_value[i] === new_value[i]) i++;
            return i;
          })();

          const unchanged = new_value?.substring(0, prefixMatchLength) ?? "";
          const changed = new_value?.substring(prefixMatchLength) ?? "";

          result.push(
            `${setTextBold(
              "Edit Description:"
            )} ${unchanged}<span style="color:#1A73E8">${changed}</span>`
          );
          break;
        case "status_id":
          result.push(
            `${setTextBold("Status:")} changed from ${mapValue(
              old_value,
              options?.statusMap
            )} to ${mapValue(new_value, options?.statusMap)}`
          );
          break;
        case "priority_id":
          result.push(
            `${setTextBold("Priority:")} changed from ${mapValue(
              old_value,
              options?.priorityMap
            )} to ${mapValue(new_value, options?.priorityMap)}`
          );
          break;
        case "start_date":
          const oldStartDate = formatISOtoBEDateTime(old_value).date;
          const newStartDate = formatISOtoBEDateTime(new_value).date;

          result.push(displayChange("Start Date:", oldStartDate, newStartDate));
          break;
        case "due_date":
          const oldDueDate = formatISOtoBEDateTime(old_value).date;
          const newDueDate = formatISOtoBEDateTime(new_value).date;

          result.push(displayChange("Due Date:", oldDueDate, newDueDate));
          break;
        case "assigned_to_id":
          const oldName = mapValue(old_value, options?.userMap);
          const newName = mapValue(new_value, options?.userMap);

          result.push(displayChange("Assgined to:", oldName, newName));
          break;
      }
    }

    if (property === "cf") {
      const fieldName = options?.customFieldMap?.[name];

      if (fieldName === "Division") {
        result.push(`${setTextBold("Division:")} changed from ${old_value} to ${new_value}`);
      }
      if (fieldName === "Causes and Solutions") {
        result.push(`${setTextBold("Edit Causes and Solutions:")} ${new_value}`);
      }
      if (fieldName === "Assigned Division") {
        result.push(
          `${setTextBold("Assinged Division:")} changed from ${old_value} to ${new_value}`
        );
      }
    }
  }

  if (notes?.trim()) {
    result.push(`<div style="margin-top: 1rem;"><i>Note:</i> ${notes.trim()}</div>`);
  }

  return result;
};

export default CardIssueList;
